import {BASE_URL} from 'config';
import request from 'utils/request';

export const UI_TRANSLATIONS_STAFF_ENDPOINT = 'ui-translations/staff';
export const UI_TRANSLATIONS_STAFF_SCHEMA = 'jdp.ui-translations.catalog.v1';

const asObject = (value) =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};

const unwrapData = (response) => {
  const payload = asObject(response);
  return asObject(payload.data && typeof payload.data === 'object' ? payload.data : payload);
};

const asString = (value, fallback = '') =>
  typeof value === 'string' ? value : fallback;

const firstString = (...values) => {
  const value = values.find((candidate) => typeof candidate === 'string');
  return typeof value === 'string' ? value : '';
};

const isSafeEntryKey = (key) => {
  if (!/^[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*$/.test(key)) return false;
  return !key
    .split('.')
    .some((segment) =>
      ['__proto__', 'prototype', 'constructor'].includes(
        segment.toLowerCase()
      )
    );
};

const asRevision = (value) => {
  const revision = Number(value);
  return Number.isSafeInteger(revision) && revision >= 0 ? revision : 0;
};

const normalizeOwnership = (value) => {
  const ownership = asString(value).toUpperCase();
  return ownership === 'GENERATED' || ownership === 'HUMAN'
    ? ownership
    : null;
};

const normalizeStatus = (value) => {
  const status = asString(value).toUpperCase();
  if (
    [
      'GENERATED',
      'HUMAN_EDITED',
      'MISSING_TRANSLATION',
      'REVIEW_NEEDED',
    ].includes(status)
  ) {
    return status;
  }
  return 'MISSING_TRANSLATION';
};

const normalizeOrigin = (value, ownership) => {
  const origin = asString(value).toUpperCase();
  if (['GENERATED', 'HUMAN', 'MISSING'].includes(origin)) return origin;
  return ownership || 'MISSING';
};

export const normalizeUiTranslationEntry = (candidate) => {
  const entry = asObject(candidate);
  const ownership = normalizeOwnership(entry.ownership);
  const currentValue =
    typeof entry.value === 'string'
      ? entry.value
      : asString(entry.currentValue);
  const status = normalizeStatus(entry.status);

  return {
    key: asString(entry.key),
    source: asString(entry.source),
    sourceHash: asString(entry.sourceHash),
    valueType: asString(entry.valueType || entry.type, 'plain_text'),
    variables: Array.isArray(entry.variables)
      ? entry.variables.filter((value) => typeof value === 'string')
      : [],
    required: Boolean(entry.required),
    context: asString(entry.context),
    locations: Array.isArray(entry.locations)
      ? entry.locations.filter((value) => typeof value === 'string')
      : [],
    examplePath: asString(entry.examplePath || entry.exampleUrl),
    currentValue,
    currentValueHash: asString(entry.currentValueHash || entry.currentHash),
    ownership,
    origin: normalizeOrigin(entry.origin, ownership),
    generatedBaselineAvailable: Boolean(
      entry.generatedBaselineAvailable ||
        typeof entry.generatedBaseline === 'string'
    ),
    generatedBaseline: asString(entry.generatedBaseline),
    reviewNeeded:
      Boolean(entry.reviewNeeded) || status === 'REVIEW_NEEDED',
    status,
  };
};

export const normalizeUiTranslationCatalog = (response) => {
  const payload = unwrapData(response);
  const catalogVersion = asString(payload.catalogVersion);
  const releaseRevision = asRevision(
    payload.releaseRevision ?? payload.headRevision
  );
  const entries = Array.isArray(payload.entries)
    ? payload.entries.map(normalizeUiTranslationEntry)
    : [];
  const validEntries =
    Array.isArray(payload.entries) &&
    new Set(entries.map((entry) => entry.key)).size === entries.length &&
    entries.every(
      (entry) =>
        entry.key &&
        entry.sourceHash &&
        isSafeEntryKey(entry.key)
    );

  return {
    schemaVersion: asString(payload.schemaVersion),
    catalogVersion,
    releaseCatalogVersion: asString(payload.releaseCatalogVersion),
    releaseRevision,
    initialReleaseComplete: Boolean(
      payload.initialMultilingualReleaseActivated ??
        payload.initialReleaseComplete
    ),
    locale: asString(payload.locale),
    namespace: asString(payload.namespace),
    entries,
    validEntries,
  };
};

const queryString = (values) => {
  const query = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.set(key, String(value));
    }
  });
  return query.toString();
};

export const getUiTranslationCatalog = async (token, {locale, namespace}) => {
  const response = await request(
    `${UI_TRANSLATIONS_STAFF_ENDPOINT}?${queryString({locale, namespace})}`,
    token
  );
  const catalog = normalizeUiTranslationCatalog(response);
  if (
    catalog.schemaVersion !== UI_TRANSLATIONS_STAFF_SCHEMA ||
    !catalog.catalogVersion ||
    catalog.locale !== locale ||
    catalog.namespace !== namespace ||
    !catalog.validEntries
  ) {
    throw new Error('The server returned an invalid UI translation catalog.');
  }
  return catalog;
};

export const publishUiTranslationChanges = async (
  token,
  body,
  idempotencyKey
) => {
  const response = await request(
    `${UI_TRANSLATIONS_STAFF_ENDPOINT}/publish`,
    token,
    {
      method: 'POST',
      headers: {'Idempotency-Key': idempotencyKey},
      body: JSON.stringify(body),
    }
  );
  const payload = unwrapData(response);
  const result = {
    releaseId: asString(payload.releaseId),
    revision: asRevision(payload.revision || payload.releaseRevision),
    catalogVersion: asString(payload.catalogVersion, body.catalogVersion),
    changedKeys: Array.isArray(payload.changedKeys) ? payload.changedKeys : [],
  };
  if (!result.releaseId || !result.revision || !result.catalogVersion) {
    throw new Error('The server returned an invalid publication result.');
  }
  return result;
};

const normalizeActor = (candidate) => {
  if (typeof candidate === 'string') {
    return {type: '', label: candidate};
  }
  const actor = asObject(candidate);
  return {
    type: asString(actor.type),
    label: asString(
      actor.displayLabel || actor.label || actor.agentLabel || actor.userId
    ),
  };
};

export const normalizeUiTranslationHistoryItem = (candidate) => {
  const item = asObject(candidate);
  return {
    releaseId: asString(item.releaseId || item.id),
    revision: asRevision(item.revision),
    catalogVersion: asString(item.catalogVersion),
    createdAt: asString(item.createdAt || item.publishedAt),
    reason: asString(item.reason),
    actor: normalizeActor(item.actor),
    changedKeys: Array.isArray(item.changedKeys) ? item.changedKeys : [],
    live: Boolean(item.live || item.isLive),
  };
};

export const getUiTranslationHistory = async (
  token,
  {locale, namespace, cursor}
) => {
  const response = await request(
    `${UI_TRANSLATIONS_STAFF_ENDPOINT}/history?${queryString({
      locale,
      namespace,
      cursor,
    })}`,
    token
  );
  const payload = unwrapData(response);
  const items = Array.isArray(payload.items || payload.history)
    ? (payload.items || payload.history).map(normalizeUiTranslationHistoryItem)
    : [];
  return {items, nextCursor: asString(payload.nextCursor)};
};

export const getUiTranslationHistoryDetail = async (
  token,
  revision,
  {locale, namespace}
) => {
  const response = await request(
    `${UI_TRANSLATIONS_STAFF_ENDPOINT}/history/${encodeURIComponent(
      revision
    )}?${queryString({locale, namespace})}`,
    token
  );
  const payload = unwrapData(response);
  return {
    ...normalizeUiTranslationHistoryItem(payload),
    previousReleaseId: asString(payload.previousReleaseId),
    snapshots: Array.isArray(payload.snapshots || payload.snapshotSummary)
      ? payload.snapshots || payload.snapshotSummary
      : [],
  };
};

export const rollbackUiTranslations = async (
  token,
  body,
  idempotencyKey
) => {
  const response = await request(
    `${UI_TRANSLATIONS_STAFF_ENDPOINT}/rollback`,
    token,
    {
      method: 'POST',
      headers: {'Idempotency-Key': idempotencyKey},
      body: JSON.stringify(body),
    }
  );
  const payload = unwrapData(response);
  const result = {
    releaseId: asString(payload.releaseId),
    revision: asRevision(payload.revision || payload.releaseRevision),
    catalogVersion: asString(payload.catalogVersion, body.catalogVersion),
  };
  if (!result.releaseId || !result.revision || !result.catalogVersion) {
    throw new Error('The server returned an invalid rollback result.');
  }
  return result;
};

export const createUiTranslationIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes)
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const getSafeUiTranslationExampleUrl = (
  examplePath,
  publicBaseUrl = BASE_URL
) => {
  const hasUnsafeCharacter =
    typeof examplePath === 'string' &&
    Array.from(examplePath).some((character) => {
      const code = character.charCodeAt(0);
      return character === '\\' || code <= 0x1f;
    });
  if (
    typeof examplePath !== 'string' ||
    !examplePath.startsWith('/') ||
    examplePath.startsWith('//') ||
    hasUnsafeCharacter
  ) {
    return '';
  }
  try {
    const base = new URL(publicBaseUrl);
    const target = new URL(examplePath, `${base.origin}/`);
    return target.origin === base.origin && ['http:', 'https:'].includes(target.protocol)
      ? target.toString()
      : '';
  } catch {
    return '';
  }
};

const errorPayload = (error) => {
  const payload = asObject(error?.payload);
  return asObject(payload.data && typeof payload.data === 'object' ? payload.data : payload);
};

export const getUiTranslationErrorCode = (error) => {
  const payload = errorPayload(error);
  const nested = asObject(payload.error);
  return asString(error?.code || payload.code || nested.code);
};

export const normalizeUiTranslationConflict = (error) => {
  const payload = errorPayload(error);
  const conflicts = Array.isArray(payload.conflicts)
    ? payload.conflicts.map((candidate) => {
        const conflict = asObject(candidate);
        const hasLocalValue = [
          conflict.localValue,
          conflict.yourValue,
          conflict.proposedValue,
        ].some((value) => typeof value === 'string');
        return {
          key: asString(conflict.key),
          localValue: firstString(
            conflict.localValue,
            conflict.yourValue,
            conflict.proposedValue
          ),
          hasLocalValue,
          currentValue: asString(conflict.currentValue),
          currentValueHash: asString(
            conflict.currentValueHash || conflict.currentHash
          ),
          currentOwnership: normalizeOwnership(conflict.currentOwnership),
          source: asString(conflict.source),
          sourceHash: asString(conflict.sourceHash),
          removedFromCatalog: Boolean(conflict.removedFromCatalog),
        };
      })
    : [];
  return {
    code: getUiTranslationErrorCode(error),
    latestRevision: asRevision(payload.latestRevision || payload.releaseRevision),
    catalogVersion: asString(payload.catalogVersion),
    conflicts: conflicts.filter((conflict) => conflict.key),
  };
};

export const formatUiTranslationRequestError = (error, action = 'load') => {
  const code = getUiTranslationErrorCode(error);
  if (code === 'UI_TRANSLATION_INITIAL_RELEASE_REQUIRED') {
    return 'The first multilingual release must be published before staff changes are available.';
  }
  const detail =
    errorPayload(error).message ||
    (typeof error?.message === 'string' ? error.message : '');
  const prefix =
    action === 'publish'
      ? 'UI translation changes could not be published.'
      : action === 'history'
        ? 'UI translation history could not be loaded.'
        : action === 'rollback'
          ? 'The selected revision could not be rolled back.'
          : 'UI translations could not be loaded.';
  return detail && detail !== prefix ? `${prefix} ${detail}` : prefix;
};
